<template>
  <div class="table-content cal-height">
    <b-modal
      :active="isOptionsModalActive"
      custom-class="modal-wrapper"
      :can-cancel="false"
    >
      <div class="content-options-wrapper" v-if="isOptionsModalActive">
        <div class="options">
          <div
            v-for="(option, index) in selectedChoiceOptions"
            :key="index"
            class="option-item"
          >
            {{ index + 1 + '.' }}
            <b-field>
              <b-input
                v-if="option.other"
                placeholder="Other"
                readonly
              ></b-input>

              <b-input
                v-else
                v-model="option.defaultMC"
                placeholder="Enter an option"
              ></b-input>
              <a class="delete-option" @click="removeOption(index)">✖</a>
            </b-field>
          </div>
        </div>
        <b-button type="is-primary" @click="addOption()"
          >+ Add an option</b-button
        >
        <b-button
          type="is-primary"
          @click="addOtherOption"
          :disabled="selectedChoiceOptions.some((opt) => opt.other)"
          >+ Add "Other" option</b-button
        >
        <div class="footer-options">
          <b-button type="is-primary" @click="onCloseOptionsModal()"
            >Cancel</b-button
          >
          <b-button type="is-success" @click="onSaveOptionsModal()"
            >Save</b-button
          >
        </div>
      </div>
    </b-modal>

    <table v-if="tableData">
      <thead>
        <tr v-if="isPartialEditMode">
          <th class="table-cell"></th>
          <th
            v-for="(colType, index) in colsQuestionTypes"
            :key="`head-${index}`"
            class="table-cell"
            style="text-align: center"
          >
            <div class="manage-type-column">
              <b-dropdown
                :scrollable="true"
                :max-height="200"
                aria-role="list"
                @change="setColumnQuestionType(index, $event)"
                :position="
                  index === colsQuestionTypes.length - 1
                    ? 'is-bottom-left'
                    : 'is-bottom-right'
                "
              >
                <template #trigger>
                  <b-button
                    :label="getLabel(colType)"
                    type="is-primary"
                    :icon-left="getIcon(colType)"
                    icon-right="menu-down"
                  />
                </template>

                <b-dropdown-item
                  v-for="(item, index) in selectionItems"
                  :key="index"
                  :value="item.type"
                  aria-role="listitem"
                >
                  <div class="media">
                    <b-icon class="media-left" :icon="item.icon"></b-icon>
                    <div class="media-content">
                      <h3>{{ item.label }}</h3>
                    </div>
                  </div>
                </b-dropdown-item>
              </b-dropdown>
              <template
                v-if="
                  colType.type === 'multiplechoice' ||
                  colType.type === 'singlechoice'
                "
              >
                <b-button
                  type="is-ghost"
                  @click="manageChoiceOptions(index + 1)"
                  >Manage Options</b-button
                >
              </template>
            </div>
          </th>
        </tr>

        <tr :class="{ 'edit-mode': isEditFirstColumnEditable }">
          <th
            v-if="isPartialEditMode"
            style="text-align: center"
            @click="toggleEditMode"
            class="table-cell"
          >
            <b-button type="is-success is-light" size="is-small">Edit</b-button>
          </th>
          <th v-else class="table-cell"></th>
          <th
            v-for="(header, index) in tableData.length > 0
              ? tableData[0].slice(1)
              : []"
            :key="`head-${index}`"
            class="table-cell"
            style="text-align: center"
            :contenteditable="isEditFirstColumnEditable"
            @input="handleHeaderInput($event, 0, index + 1)"
            @focus="onHeaderFocus($event)"
          >
            {{ header }}
          </th>
        </tr>
      </thead>
      <transition-group name="row-bounce" tag="tbody">
        <tr
          v-for="(row, rowIndex) in tableData.slice(1)"
          :key="`row-${rowIndex}`"
          :class="{
            'odd-row': rowIndex % 2 !== 0,
            'even-row': rowIndex % 2 === 0,
          }"
        >
          <template v-for="(cell, colIndex) in row">
            <td
              v-if="colIndex === 0"
              class="table-cell"
              :class="{
                'editable-cell': isEditFirstColumnEditable && colIndex === 0,
                'editable-cell-background':
                  isEditFirstColumnEditable && colIndex === 0,
              }"
              :contenteditable="isCellContentEditable(colIndex)"
              @input="handleHeaderInput($event, rowIndex + 1, colIndex)"
              @focus="onCellFocus(rowIndex + 1, colIndex, $event)"
              v-html="cell"
            ></td>

            <td
              v-else
              class="table-cell"
              :class="{
                'editable-cell': isEditFirstColumnEditable && colIndex === 0,
                'editable-cell-background':
                  isEditFirstColumnEditable && colIndex === 0,
              }"
              @focus="onCellFocus(rowIndex, colIndex)"
              tabindex="0"
            >
              <div
                class="question-details"
                v-if="row[colIndex].type === 'action'"
              >
                <b-checkbox
                  type="is-success"
                  size="is-medium"
                  :disabled="!isInspectorMode"
                  v-model="row[colIndex].value"
                  @input="(value) => handleInput(value, rowIndex + 1, colIndex)"
                >
                  Done
                </b-checkbox>
              </div>

              <div
                class="question-details question-details-yes-no"
                v-if="row[colIndex].type === 'yesno'"
              >
                <b-radio-button
                  :disabled="!isInspectorMode"
                  v-for="option in yesNoOptions"
                  :key="option.value"
                  :native-value="option.value"
                  size="is-medium"
                  :type="option.type"
                  v-model="row[colIndex].value"
                  @input="(value) => handleInput(value, rowIndex + 1, colIndex)"
                >
                  <b-icon :icon="option.icon"></b-icon>
                  <span>{{ option.label }}</span>
                </b-radio-button>
              </div>

              <div
                class="question-details"
                v-if="row[colIndex].type === 'textnumber'"
              >
                <RichTextEditor
                  v-model="row[colIndex].value"
                  :disabled="!isCellContentEditable(colIndex)"
                  @input="(value) => handleInput(value, rowIndex + 1, colIndex)"
                  :headings="[]"
                  :autoFocusOnMount="true"
                  v-if="
                    rowIndex === focusedCell.row &&
                    colIndex === focusedCell.col &&
                    isCellContentEditable(colIndex)
                  "
                />
                <DisplayRichTextEditor v-else :value="row[colIndex].value" />
              </div>

              <div
                v-if="row[colIndex].type === 'multiplechoice'"
                class="question-details"
                style="
                  display: flex;
                  gap: 10px;
                  align-items: flex-start;
                  flex-direction: column;
                "
              >
                <template>
                  <template v-for="option in row[colIndex].options">
                    <b-checkbox
                      v-if="!option.other"
                      size="is-medium"
                      v-model="option.checked"
                      type="is-success"
                      :disabled="!isInspectorMode"
                      @input="
                        (value) =>
                          handelChoiceInput(
                            value,
                            option,
                            rowIndex + 1,
                            colIndex
                          )
                      "
                    >
                      {{
                        option.text?.defaultMC ||
                        option?.text ||
                        option.defaultMC
                      }}
                    </b-checkbox>

                    <div class="other-option" v-if="option.other">
                      <b-checkbox
                        size="is-medium"
                        type="is-success"
                        :disabled="!isInspectorMode"
                        :value="
                          row[colIndex].options.find((opt) => opt.other)
                            ?.checked || false
                        "
                        @input="
                          (value) =>
                            handelChoiceInput(
                              value,
                              option,
                              rowIndex + 1,
                              colIndex
                            )
                        "
                      >
                      </b-checkbox>
                      <b-input
                        :disabled="!isInspectorMode"
                        placeholder="Other"
                        :readonly="!hasOtherOption(row[colIndex])"
                        @input="
                          inputOtherOption(
                            row[colIndex],
                            $event,
                            rowIndex + 1,
                            colIndex
                          )
                        "
                        :value="
                          row[colIndex].options.find((opt) => opt.other)
                            ?.defaultMC
                        "
                      ></b-input>
                    </div>
                  </template>
                </template>
              </div>

              <div
                v-if="row[colIndex].type === 'singlechoice'"
                class="question-details"
                style="
                  display: flex;
                  gap: 10px;
                  align-items: flex-start;
                  flex-direction: column;
                "
              >
                <template
                  v-for="(option, optionIndex) in row[colIndex].options"
                >
                  <b-radio
                    v-if="!option.other"
                    size="is-medium"
                    :value="!option.checked"
                    :disabled="!isInspectorMode"
                    type="is-success"
                    @input="
                      radioOptionChanged(
                        row[colIndex],
                        optionIndex,
                        rowIndex + 1,
                        colIndex
                      )
                    "
                  >
                    {{
                      option.text?.defaultMC || option?.text || option.defaultMC
                    }}
                  </b-radio>
                  <div class="other-option" v-if="option.other">
                    <b-radio
                      size="is-medium"
                      type="is-success"
                      :disabled="!isInspectorMode"
                      @input="
                        radioOptionChanged(
                          row[colIndex],
                          optionIndex,
                          rowIndex + 1,
                          colIndex
                        )
                      "
                      :value="
                        !row[colIndex].options.find((opt) => opt.other)
                          ?.checked || false
                      "
                    >
                    </b-radio>
                    <b-input
                      placeholder="Other"
                      :disabled="!isInspectorMode"
                      :readonly="!hasOtherOption(row[colIndex])"
                      @input="
                        inputOtherOption(
                          row[colIndex],
                          $event,
                          rowIndex + 1,
                          colIndex
                        )
                      "
                      :value="
                        row[colIndex].options.find((opt) => opt.other)
                          ?.defaultMC
                      "
                    ></b-input>
                  </div>
                </template>
              </div>
            </td>
          </template>
        </tr>
      </transition-group>
    </table>
  </div>
</template>

<script>
import RichTextEditor from '../../../RichTextEditor';
import DisplayRichTextEditor from '../../../RichTextEditor/DisplayRichTextEditor';

export default {
  props: {
    rows: {
      type: Number,
      required: true,
    },
    cols: {
      type: Number,
      required: true,
    },
    data: {
      type: Array,
      required: true,
    },
    displayType: {
      type: String,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    isCSV: {
      type: Boolean,
      required: false,
      default: false,
    },
    onlyNumbers: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  components: {
    RichTextEditor,
    DisplayRichTextEditor,
  },

  computed: {
    isInspectorMode() {
      return this.displayType === 'inspector';
    },
    isPartialEditMode() {
      return this.displayType === 'partialEdit';
    },
    colsQuestionTypes() {
      if (!this.tempTableData || this.tempTableData.length === 0) {
        return [];
      }
      return this.tempTableData[1].slice(1);
    },
    user() {
      return this.$store.state.currentUser;
    },
  },
  mounted() {
    this.isEditFirstColumnEditable = false;

    this.tableData = this.data;
    // Check if all rows have the same length, if not, pad with empty strings
    this.tableData.forEach((row) => {
      while (row.length < this.tableData[0].length) {
        row.push('');
      }
    });

    if (this.tableData.length === 0 || this.displayType === 'inspector') {
      this.createTableData();
    } else {
      this.initTempData();
    }
  },
  watch: {
    rows() {
      this.createTableData();
    },
    cols() {
      this.createTableData();
    },
    data(newData) {
      if (this.displayType === 'preview') {
        this.tableData = newData;
        this.initTempData();
      }
    },
  },
  data() {
    return {
      isEditFirstColumnEditable: false,
      tableData: [],
      tempTableData: [],
      focusedCell: { row: null, col: null },
      yesNoOptions: [
        {
          value: 'no',
          label: 'No',
          icon: 'close',
          type: 'is-danger is-light is-outlined',
        },
        {
          value: 'yes',
          label: 'Yes',
          icon: 'check',
          type: 'is-success is-light is-outlined',
        },
      ],
      selectionItems: [
        { type: 'action', label: 'Action', icon: 'check' },
        { type: 'textnumber', label: 'Text', icon: 'pencil' },
        { type: 'yesno', label: 'Yes/No', icon: 'help' },
        { type: 'multiplechoice', label: 'Multiple Choice', icon: 'menu' },
        {
          type: 'singlechoice',
          label: 'Single Choice',
          icon: 'radiobox-marked',
        },
      ],
      isOptionsModalActive: false,
      choiseOptionsIndex: null,
      selectedChoiceOptions: [],
    };
  },
  methods: {
    removeOption(index) {
      this.selectedChoiceOptions.splice(index, 1);
    },

    addOtherOption() {
      this.selectedChoiceOptions.push({
        checked: false,
        defaultMC: '',
        other: true,
      });
    },

    onCellFocus(row, col, event) {
      if (event) {
        this.onHeaderFocus(event);
      }
      this.focusedCell = { row, col };
    },

    getLabel(colType) {
      const found = this.selectionItems.find(
        (item) => item.type === colType.type
      );
      return found ? found.label : 'Select Type';
    },

    getIcon(colType) {
      const found = this.selectionItems.find(
        (item) => item.type === colType.type
      );
      return found ? found.icon : 'help';
    },

    sortOptions() {
      this.selectedChoiceOptions.sort(
        (a, b) => (a.other ? 1 : 0) - (b.other ? 1 : 0)
      );
    },

    addOption() {
      this.selectedChoiceOptions.push({
        checked: false,
        defaultMC: '',
      });

      this.sortOptions();
    },

    manageChoiceOptions(index) {
      this.choiseOptionsIndex = index;

      let startOptions = this.tempTableData.slice(1)[0][index].options;
      if (!startOptions || startOptions.length === 0) {
        startOptions = [{ other: false, defaultMC: '', checked: false }];
      }

      this.selectedChoiceOptions = [...startOptions];
      this.sortOptions();
      this.isOptionsModalActive = true;
    },

    onSaveOptionsModal() {
      this.tempTableData.slice(1).forEach((row) => {
        row[this.choiseOptionsIndex].options = this.selectedChoiceOptions;
      });

      this.onCloseOptionsModal();
    },

    onCloseOptionsModal() {
      this.choiseOptionsIndex = null;
      this.selectedChoiceOptions = [];
      this.isOptionsModalActive = false;
    },

    setColumnQuestionType(index, type) {
      this.tempTableData.slice(1).forEach((row) => {
        row[index + 1].type = type;
        switch (type) {
          case 'action':
            row[index + 1].value = false;
            break;

          case 'textnumber':
            row[index + 1].value = '';
            break;

          case 'yesno':
            row[index + 1].value = '';
            break;

          case 'multiplechoice':
            row[index + 1].options = [];
            break;

          case 'singlechoice':
            row[index + 1].options = [];
            break;

          default:
            break;
        }
      });
    },

    resetFocusedCell() {
      this.focusedCell = { row: null, col: null };
    },

    getTempTableData() {
      return this.tempTableData;
    },
    initTempData() {
      this.tempTableData = this.tableData.map((row) => [...row]);
    },

    handleInput(value, rowIndex, colIndex) {
      this.tempTableData[rowIndex][colIndex].value = value;
      this.tempTableData[rowIndex][colIndex].lastUpdatedBy = this.user.fullname;
      this.tempTableData[rowIndex][colIndex].lastModifiedAt =
        new Date().toISOString();
      new Date().toISOString();
      this.$emit(
        'onCellInput',
        rowIndex,
        colIndex,
        this.tempTableData[rowIndex][colIndex]
      );
    },

    handelChoiceInput(value, option, rowIndex, colIndex) {
      option.checked = value;
      this.tempTableData[rowIndex][colIndex].lastUpdatedBy = this.user.fullname;
      this.tempTableData[rowIndex][colIndex].lastModifiedAt =
        new Date().toISOString();
      this.$emit(
        'onCellInput',
        rowIndex,
        colIndex,
        this.tempTableData[rowIndex][colIndex]
      );
    },

    hasOtherOption(cell) {
      return cell.options.find((opt) => opt.other)?.checked || false;
    },

    inputOtherOption(cell, event, rowIndex, colIndex) {
      this.tempTableData[rowIndex][colIndex].lastUpdatedBy = this.user.fullname;
      this.tempTableData[rowIndex][colIndex].lastModifiedAt =
        new Date().toISOString();
      const otherOption = cell.options.find((opt) => opt.other);
      if (otherOption) {
        otherOption.defaultMC = event;
        this.$emit(
          'onCellInput',
          rowIndex,
          colIndex,
          this.tempTableData[rowIndex][colIndex]
        );
      }
    },

    radioOptionChanged(cell, optionIndex, rowIndex, colIndex) {
      this.tempTableData[rowIndex][colIndex].lastUpdatedBy = this.user.fullname;
      this.tempTableData[rowIndex][colIndex].lastModifiedAt =
        new Date().toISOString();
      cell.options.forEach((opt, idx) => {
        opt.checked = idx === optionIndex;
      });
      this.$emit(
        'onCellInput',
        rowIndex,
        colIndex,
        this.tempTableData[rowIndex][colIndex]
      );
    },

    handleHeaderInput(event, rowIndex, colIndex) {
      const value = event.target.innerText;
      this.tempTableData[rowIndex][colIndex] = value;
      this.$emit('onCellInput', rowIndex, colIndex, value);
    },

    isCellContentEditable(colIndex) {
      if (this.readOnly) {
        return false;
      }

      if (
        this.isEditFirstColumnEditable &&
        this.isPartialEditMode &&
        colIndex === 0
      ) {
        return true;
      }

      if (this.isInspectorMode && colIndex !== 0) {
        return true;
      }

      return false;
    },
    toggleEditMode() {
      this.isEditFirstColumnEditable = !this.isEditFirstColumnEditable;
      if (!this.isEditFirstColumnEditable) {
        this.tableData = this.tempTableData.map((row) => [...row]);
        this.$emit('tableDataChanged', this.tableData);
      }
    },
    createTableData() {
      let oldTableData = [...this.tableData];
      if (this.tempTableData.length > 0) {
        oldTableData = [...this.tempTableData];
      }

      if (this.isInspectorMode) {
        // Use original data structure without headers/index column
        this.tableData = oldTableData.length
          ? oldTableData
          : Array.from({ length: this.rows }, () =>
              Array.from({ length: this.cols }, () => '')
            );
      } else {
        const headers = ['', ...Array.from({ length: this.cols }, () => '-')];

        this.tableData = [headers].concat(
          Array.from({ length: this.rows }, (_, rowIndex) =>
            Array.from({ length: this.cols + 1 }, (colValue, colIndex) => {
              let cellToAdd = { type: 'action', value: '' };

              if (
                oldTableData[1] &&
                colIndex > 0 &&
                oldTableData[1][colIndex]
              ) {
                cellToAdd = JSON.parse(
                  JSON.stringify(oldTableData[1][colIndex])
                );
                cellToAdd.value = '';
              }

              if (
                cellToAdd.type === 'multiplechoice' ||
                cellToAdd.type === 'singlechoice'
              ) {
                cellToAdd.options.forEach((opt) => {
                  opt.checked = false;
                  if (opt.other) {
                    opt.defaultMC = '';
                  }
                });
              }

              return colIndex === 0 ? String(rowIndex + 1) : cellToAdd;
            })
          )
        );

        oldTableData.forEach((row, rowIndex) => {
          row.forEach((cell, cellIndex) => {
            if (
              this.tableData[rowIndex] &&
              this.tableData[rowIndex][cellIndex] !== undefined
            ) {
              this.tableData[rowIndex][cellIndex] = cell;
            }
          });
        });
      }

      this.initTempData();
      this.$emit('tableDataChanged', this.tableData);
    },
    onHeaderFocus(event) {
      // To select all text when user clicks on header cell
      const el = event.target;
      this.$nextTick(() => {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(el);
        selection.removeAllRanges();
        selection.addRange(range);
      });
    },
  },
};
</script>

<style scoped lang="scss">
.table-content {
  overflow: scroll;
  overflow-x: hidden;
  width: 100%;

  thead {
    position: sticky;
    top: -2px;
    background: #f9fafb;
    z-index: 2;
  }
}

.other-option {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;

  .control {
    width: 100%;
  }
}

.multiplechoice-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cal-height {
  max-height: calc(100vh - 500px);
}

.row-bounce-enter-active {
  animation: bounceIn 0.8s ease forwards;
}

.row-bounce-leave-active {
  transition: opacity 0.1s ease;
  opacity: 0;
}

.edit-mode,
.editable-cell-background {
  background-color: #e0ffe4;
}

thead.edit-mode th {
  background-color: #e0ffe4;
}
table {
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;
  border: #f5f5f5 3px solid;
}

.table-cell {
  padding: 8px;
  text-align: left;
  box-sizing: border-box;
}

.table-cell:not(:last-child) {
  border-right: 1px solid #ddd;
}

.table-cell:first-child {
  width: 100px;
  text-align: center;
  overflow: hidden;
}

.odd-row {
  background-color: #ffffff;
}

.even-row {
  background-color: #dddddd48;
}

.content-options-wrapper {
  background: white;
  display: flex;
  flex-direction: column;
  width: 50%;
  border-radius: 10px;
  padding: 38px 38px 0 38px;
  margin: 0 25%;
  gap: 10px;
}

.manage-type-column {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.footer-options {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
  margin-bottom: 38px;
}

.options {
  width: 100%;
  gap: 10px;
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.option-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.delete-option {
  margin-left: 10px;
  cursor: pointer;
  color: red;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

@keyframes bounceIn {
  0% {
    transform: translateY(-10px);
    opacity: 0;
  }
  30% {
    transform: translateY(0);
    opacity: 1;
  }
  50% {
    transform: translateY(-10px);
  }

  80% {
    transform: translateY(-0px);
  }

  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
